import { usePrevious } from '@sequencehq/utils'
import {
  Button,
  Link,
  Primary14ExtendedRegular,
  YellowYellow10,
  YellowYellow80,
  IconArrowRightLeft,
  Box,
  Grid
} from '@sequencehq/core-components'
import { FC, useEffect } from 'react'
import {
  useAvailableFeaturesForPhase,
  useCubeData,
  useMutators,
  useStartDate,
  useStatus
} from 'Cube/domain'
import { useLogEvent } from 'lib/postHog/useLogEvent'
import { quoteStatusAdapter } from 'Cube/utils/status.adapter'
import { add, startOfMonth } from '@sequencehq/utils/dates'

export const usePhaseAlignmentWarning = (props: { phaseId: string }) => {
  const { startDate, setStartDate } = useStartDate()
  const { mutators } = useMutators()
  const { availableFeaturesForPhase } = useAvailableFeaturesForPhase({
    phaseId: props.phaseId
  })
  const { isAQuote } = useStatus()

  return {
    alignmentButton: {
      onClick: () => {
        /**
         * The alignment process is fiddly, and relies on having some kind of billing date
         * to work against, as well as absolute dates for the phases. Therefore, to make it
         * work at all, we need to ensure the start date is set. When a quote (since only
         * quote has this ability) has no start date, we presume the 1st of the month as
         * the billing day (also weird, but we need to roll with something). Therefore, setting
         * the date to the 1st of the next month makes the most sense here in terms of trying
         * to align things.
         *
         * Will the UX be amazing in this scenario? No, not really, and realistically when a user
         * starts running into these alignment issues in quotes they're probably going to need
         * to change things up, but at least we can offer a solution that does ostensibly work!
         */
        if (!startDate) {
          const startOfNextMonth = startOfMonth(add(new Date(), { months: 1 }))
          setStartDate(startOfNextMonth)
        }
        mutators.alignPhaseDuration(props.phaseId)
      }
    },
    visible: availableFeaturesForPhase.phase.dates.alignDates.available.visible,
    showQuoteText: isAQuote
  }
}

export const logAlignmentWarningShown = (props: { visible: boolean }) => {
  const { logEvent } = useLogEvent()
  const { data } = useCubeData()
  const prevAlignWarningVisibility = usePrevious(props.visible)

  useEffect(() => {
    if (props.visible && prevAlignWarningVisibility !== props.visible) {
      logEvent({
        eventName: 'cube_phase_alignment_warning_visible',
        metadata: quoteStatusAdapter.out(data.common.status)
          ? {
              quoteId: data.common.id
            }
          : { scheduleId: data.common.id }
      })
    }
  }, [
    prevAlignWarningVisibility,
    props.visible,
    logEvent,
    data.common.status,
    data.common.id
  ])
}

const WARNING_TEXT =
  'The selected phase dates fall within a billing period and restrict price edits, discounts, and minimums.'
const ADDITIONAL_QUOTE_ALIGNMENT_WARNING_TEXT =
  'For quotes we use the start date as the billing date, or the 1st if no start date has been set.'

export const PhaseAlignmentWarning: FC<{
  phaseId: string
}> = ({ phaseId }) => {
  const { visible, alignmentButton, showQuoteText } = usePhaseAlignmentWarning({
    phaseId
  })
  logAlignmentWarningShown({ visible })

  if (!visible) {
    return null
  }

  return (
    <Grid
      alignItems="center"
      color={YellowYellow80}
      borderRadius="8px"
      backgroundColor={YellowYellow10}
      padding="12px 16px"
      width="100%"
      {...Primary14ExtendedRegular}
      marginBottom="16px"
      templateColumns="1fr auto"
      columnGap="36px"
      data-testid="phase.alignmentWarning"
    >
      <Box>
        {WARNING_TEXT}{' '}
        {showQuoteText ? ADDITIONAL_QUOTE_ALIGNMENT_WARNING_TEXT : ''}{' '}
        <Link.Unstyled
          to="https://docs.sequencehq.com/billing/billing-schedule-phases"
          target="_blank"
          style={{ fontWeight: '600' }}
        >
          Learn more
        </Link.Unstyled>
      </Box>
      <Button
        variant="secondary"
        style={{ height: '36px', minWidth: '36px' }}
        title="Align dates to billing period"
        aria-label="Align dates to billing period"
        onClick={alignmentButton.onClick}
        data-testid="phase.alignDates"
        leadingIcon={<IconArrowRightLeft />}
      >
        Align dates
      </Button>
    </Grid>
  )
}
